/* eslint-disable react/prop-types */
import React from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { UPDATE_SOLICITATION } from '../../queries';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';
import ContractYearChangeWarnModal from '../../components/upiid-details/contract-year-change-warn-modal';
import UpiidDetailsForm from '../../components/upiid-details/upiid-details-form';
import { TO_CONTINUE_FIELD } from '../../constants';
import useSimpleAlertModal from '@/hooks/use-simple-alert-modal';
import { isSolicitationDetailsDataDirty } from './is-data-dirty';
import {
  getUpiidDetailsNextPath,
  parseJSONErrorMessage,
} from '../../utils/solicitation-helpers';
import { useUpiidDetails } from '../../components/upiid-details/upiid-details-provider';
import { pageErrorNotifications } from '../../../../utilities/error-handling';

const EditSolicitationForm = () => {
  const navigate = useNavigate();
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );
  const {
    isModalOpen: showContractYearChangeModal,
    handleToggleModal: toggleContractYearChangeModal,
    handleOnModalProceed,
  } = useSimpleAlertModal();
  const { solicitation, refetchSolicitation } = useUpiidDetails();

  const navigateToNextPage = (bidType) => {
    navigate(getUpiidDetailsNextPath(bidType, solicitation.id));
  };

  const [updateSolicitation] = useMutation(UPDATE_SOLICITATION, {
    onError: (requestError) => {
      setSolicitationNotification([
        ...pageErrorNotifications(requestError, {
          id: 'UPDATE_SOLICITATION_ERROR',
          message: parseJSONErrorMessage(requestError.message),
          type: 'error',
          closeable: false,
          showInModal: false,
        }),
      ]);
    },
  });

  const onSubmit = (data) => {
    setSolicitationNotification([]);
    const goNext = data[TO_CONTINUE_FIELD];

    if (
      `${solicitation.contractYear}` !== `${data.contractYear}` &&
      !showContractYearChangeModal
    ) {
      toggleContractYearChangeModal(data);
    }
    // before blindly updating the solicitation, check if there is any change in the data(isDataDirty)
    else if (isSolicitationDetailsDataDirty(solicitation, data)) {
      void updateSolicitation({
        variables: {
          solicitationinput: {
            solicitationID: Number(solicitation.id),
            solicitationNumber: (data.upiidAAC + data.upiidSQ).toUpperCase(),
            title: data.solicitationTitle,
            contractYear: Number(data.contractYear),
            purchaseTypeCode: data.purchaseType,
            solicitationType: data.solicitationProcess,
            description: data.solicitationDescription,
            samUrl: data.solicitationPosting,
            bidType: data.bidType,
          },
        },
        onCompleted: (response) => {
          // set success message
          setSolicitationNotification([
            {
              id: 'UPDATE_SOLICITATION',
              message: `Solicitation has been successfully updated.`,
              type: 'success',
              closeable: true,
              showInModal: false,
              showOnNextPage: goNext,
            },
          ]);
          if (goNext) navigateToNextPage(response.updateSolicitation.bidType);
          else refetchSolicitation();
        },
      });
    } else if (goNext) {
      navigateToNextPage(solicitation.bidType);
    }
  };

  return (
    <>
      <ContractYearChangeWarnModal
        isOpen={showContractYearChangeModal}
        onClose={toggleContractYearChangeModal}
        onProceed={handleOnModalProceed(onSubmit)}
      />
      <UpiidDetailsForm
        formId="edit-solicitation-form"
        onSubmit={onSubmit}
        solicitation={solicitation}
      />
    </>
  );
};
export default EditSolicitationForm;
